import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  useTheme, 
  CircularProgress, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  ToggleButtonGroup, 
  ToggleButton, 
  Switch 
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';

const PricingSection = ({ pricingData, loadingPrices, currency, setCurrency, isYearly, setIsYearly }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleChoosePlan = (plan) => {
    if (plan.toLowerCase() === 'business') {
      navigate(ROUTES.CONTACT_SALES);
    } else {
      navigate(`${ROUTES.SIGNUP}?plan=${plan.toLowerCase()}`);
    }
  };

  const handleCurrencyChange = (event, newCurrency) => {
    if (newCurrency !== null) {
      setCurrency(newCurrency);
    }
  };

  if (loadingPrices) {
    return (
      <Container maxWidth="lg" sx={{ py: 15, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!pricingData || !pricingData.plans || pricingData.plans.length === 0) {
    return null;
  }

  const getDisplayPrice = (plan) => {
    const currencyKey = currency.toLowerCase();
    const interval = isYearly ? 'yearly' : 'monthly';
    if (plan.prices && plan.prices[currencyKey]) {
      const price = plan.prices[currencyKey][interval];
      const displayPrice = isYearly ? (price / 12).toFixed(2) : price;
      return {
        price: displayPrice,
        suffix: '/mo',
        fullPrice: price,
        priceId: plan.prices[currencyKey][`${interval}PriceId`]
      };
    }
    return { price: 0, suffix: '/mo', fullPrice: 0, priceId: null };
  };

  const pricingPlans = pricingData.plans;

  return (
    <Box sx={{ py: { xs: 10, md: 15 }, background: '#000000' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.04em',
                lineHeight: 1
              }}
            >
              Simple plans for any stage.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                mb: 6,
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
                color: theme.palette.text.secondary,
                lineHeight: 1.6
              }}
            >
              Start for free with core tools. Upgrade when you need advanced 
              AI insights and deeper automation.
            </Typography>
          </motion.div>

          {/* Controls */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              justifyContent: 'center',
              alignItems: 'center', 
              gap: 3, 
              mb: 8 
            }}
          >
            <ToggleButtonGroup
              value={currency}
              exclusive
              onChange={handleCurrencyChange}
              sx={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: 3,
                p: 0.5,
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: 2,
                  color: theme.palette.text.secondary,
                  px: 3,
                  py: 0.5,
                  fontWeight: 600,
                  '&.Mui-selected': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff'
                  }
                }
              }}
            >
              <ToggleButton value="usd">USD</ToggleButton>
              <ToggleButton value="cad">CAD</ToggleButton>
            </ToggleButtonGroup>

            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                p: 1,
                px: 3,
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: !isYearly ? '#ffffff' : theme.palette.text.secondary }}>Monthly</Typography>
              <Switch
                checked={isYearly}
                onChange={(e) => setIsYearly(e.target.checked)}
                color="primary"
              />
              <Typography variant="body2" sx={{ fontWeight: 600, color: isYearly ? '#ffffff' : theme.palette.text.secondary }}>Yearly</Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: `repeat(${pricingPlans.length}, 1fr)`
            },
            gap: 3,
            alignItems: 'stretch'
          }}
        >
          {pricingPlans.map((plan, index) => {
            const priceInfo = getDisplayPrice(plan);
            const currencySymbol = currency.toUpperCase() === 'CAD' ? 'C$' : '$';
            
            return (
              <motion.div
                key={plan.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    ...theme.customEffects?.glass,
                    background: plan.popular ? 'rgba(255, 255, 255, 0.05)' : theme.customEffects?.glass?.background,
                    borderColor: plan.popular ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.08)',
                    p: 4,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: plan.popular ? theme.palette.primary.light : 'rgba(255, 255, 255, 0.15)',
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, color: '#ffffff' }}>
                    {plan.title}
                  </Typography>

                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#ffffff', display: 'inline' }}>
                      {currencySymbol}{priceInfo.price}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'inline', color: theme.palette.text.secondary, ml: 1 }}>
                      {priceInfo.suffix}
                    </Typography>
                  </Box>

                  <List sx={{ mb: 4, flexGrow: 1 }}>
                    {plan.features.slice(0, 6).map((feature, fIndex) => (
                      <ListItem key={fIndex} disableGutters sx={{ py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 28, mt: 0.2 }}>
                          <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)' }} 
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    fullWidth
                    variant={plan.popular ? 'contained' : 'outlined'}
                    size="large"
                    onClick={() => handleChoosePlan(plan.title)}
                    sx={{ py: 1.5, fontWeight: 700 }}
                  >
                    {plan.buttonText || 'Choose Plan'}
                  </Button>
                </Card>
              </motion.div>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default PricingSection;